import {Box, Container} from "@mui/material";
import {experimentalStyled as styled} from "@mui/material/styles";
import {map} from "lodash";
import React from "react";
import {toast} from "sonner";
import {IconCopy} from "../../consumer/bounce-ui";
import FormValidations from "../../consumer/helpers/FormValidations";
import {SearchUserRead} from "../../declarations";
import {prettifyCurrencyForUndefined, useMobile} from "../../helpers";
import {IconPerson} from "../assets";
import {Colors} from "../colors";
import {Chip} from "./Chip";
import {FirstPartyChip} from "./FirstPartyChip";
import {IconButton} from "./IconButton";
import {Text} from "./Text";
import UserDetail from "./UserDetail";

interface UserPreviewProps {
  user: SearchUserRead;
  onClick: () => void;
  numberOfItems: number;
  multiAccountVariant?:
    | "inherit"
    | "yellow"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning"
    | undefined;
}

const RootStyle = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  padding: "16px",
  gap: "12px",
  cursor: "pointer",
  backgroundColor: Colors.bw.white,
  borderRadius: "16px",
  border: `1px solid ${Colors.tertiary._300}`,
  margin: "5px",
  boxShadow: "0px 1px 3px rgba(235, 233, 228, 0.5)",
  transition: "all 0.25s ease",
  "&:hover": {
    boxShadow: "0px 5px 10px rgba(235, 233, 228, 0.8)",
    transform: "translateY(-2px)",
  },
}));

const UserTitle = styled("div")(() => ({
  display: "flex",
  width: "100%",
  alignItems: "center",
  justifyContent: "space-between",
}));

const UserName = styled("div")(() => ({
  display: "flex",
  gap: "8px",
  alignItems: "center",
}));

const UserDetailsStyle = styled("div")(({theme}) => ({
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
  gridTemplateRows: "1fr 1fr 1fr",
  height: "70px",
  color: theme.palette.common.black,
}));

const getUserDetails = (user: SearchUserRead) => {
  const accountInfo = user.account_info;
  if (user.is_pre_charge_off) {
    return [
      {detailName: "SSN", detail: user.ssn},
      {detailName: "Account", detail: accountInfo?.external_account_id},
      {detailName: "Days past due", detail: accountInfo?.days_delinquent?.toString()},
      {detailName: "Phone", detail: FormValidations.convertInternationalIntoUSFormat(user.phone)},
      {detailName: "Email", detail: user.email},
      {detailName: "Delinquent balance", detail: prettifyCurrencyForUndefined(accountInfo?.total_balance)},
    ];
  }

  return [
    {detailName: "SSN", detail: user.ssn},
    {detailName: "Original Account", detail: accountInfo?.external_account_id},
    {detailName: "Bounce reference #", detail: accountInfo?.public_id, copyValue: accountInfo?.public_id},
    {detailName: "Phone", detail: FormValidations.convertInternationalIntoUSFormat(user.phone)},
    {detailName: "Email", detail: user.email},
    {detailName: "Original Creditor", detail: accountInfo?.original_creditor},
  ];
};

const UserDetailWrapper = (detail: ReturnType<typeof getUserDetails>[number]) => (
  <Box key={detail.detailName} sx={{display: "flex", alignItems: "center", overflow: "hidden", minWidth: 0}}>
    <UserDetail detailName={detail.detailName} detail={detail.detail} />
    {detail.copyValue && (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(detail.copyValue!);
          toast.success(`Copied: ${detail.copyValue}`);
        }}
        sx={{padding: "2px", marginLeft: "2px"}}
      >
        <IconCopy style={{width: "14px", height: "14px"}} />
      </IconButton>
    )}
  </Box>
);

const UserPreview = ({user, onClick, multiAccountVariant}: UserPreviewProps) => {
  const isMobile = useMobile();
  if (!user) return null;
  const details = getUserDetails(user as SearchUserRead);
  return (
    <RootStyle onClick={onClick} width={isMobile ? "350px" : "650px"}>
      <UserTitle>
        <UserName>
          <IconPerson color={Colors.grey._1000} />
          <Text variant={"interDisplay600"}>
            {`${user.first_name} ${user.middle_name ? user.middle_name : ""} ${user.last_name}`}
          </Text>
          {((!user.is_first_party && user.num_of_accounts > 1) ||
            (user.is_first_party && user.num_of_fp_accounts > 1)) && (
            <Chip label={"Multi Account"} variant_={multiAccountVariant || "secondary"} size_={"small"} border />
          )}
          {user.is_first_party && <FirstPartyChip />}
          {user.is_pre_charge_off && <Chip label={"Pre-Charge off"} variant_={"yellow"} size_={"small"} border />}
        </UserName>
        <Box>{user.logo_url && <Box component="img" src={user.logo_url} alt="logo" height={"24px"} />}</Box>
      </UserTitle>
      {isMobile ? (
        <Container>{map(details, UserDetailWrapper)}</Container>
      ) : (
        <UserDetailsStyle>{map(details, UserDetailWrapper)}</UserDetailsStyle>
      )}
    </RootStyle>
  );
};

export default UserPreview;
